import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  class: "d-flex",
  style: {"min-height":"45vw"}
}
const _hoisted_2 = { class: "content p-4 w-100" }
const _hoisted_3 = { class: "d-flex justify-content-between mb-1 mt-0" }
const _hoisted_4 = { class: "card-title mt-0" }

import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import subscribeService from '@/services/subscribe/SubscribeServiceFabrick';
import Api from "@/services/api/ApiServiceFabrick";
import getVehicleResponse from '@/types/responses/getVehicle';
import Vehicle from '@/types/vehicle';
import i18n from '@/lang/i18n';
import getVehicleEvent from '@/types/responses/getVehicleEvent';
import VehicleTimedPosition from '@/types/VehicleTimedPosition';
import VehicleMap from '@/components/VehicleInstall/VehicleMap.vue';
import EventVideoList from '@/components/VehicleInstall/EventVideoList.vue';
import VehicleTabs from '@/components/VehicleTabs.vue';
import { MANAGEMENT_OFFICE_TYPE, SUB_MANAGEMENT_OFFICE_TYPE, INSTALLATION_OFFICE_TYPE, DRIVING_ALERT_EVENT_TYPES } from '@/services/consts';
import { formatUnixToDateTimeWithMilliseconds } from '@/services/functions';
import { vehiclePositionSourceType as sourceType } from '@/services/consts';
import { TRACKING_SERVICE_DOCOMAP } from '@/services/consts/operation_office';


export default /*@__PURE__*/_defineComponent({
  __name: 'VehicleInstall',
  props: {
    showFlash: { type: Function }
  },
  setup(__props: any) {

const props = __props;
const route = useRoute();
const router = useRouter();
const vehiclePosition = ref<VehicleTimedPosition | null>(null);
const vehicleInfo = ref<getVehicleResponse | null>(null);
const vehicleEvents = ref<getVehicleEvent[]>([]);
const subscriber = ref<any>(null);
const deliveryPlanId = ref<string | null>(null);
const isLoadingFinished = ref(false);
const mapErrorMessage = ref('');
const eventVideoListErrorMessage = ref('');
const zoomLevel = ref(5);
const isMarkerVisible = ref(false);
const selfOfficeType = ref('');
const isManagementOffice = ref(false);
const isSubManagementOffice = ref(false);
const isInstallationOffice = ref(false);

async function getVehicleInfo(vehicleId: number): Promise<getVehicleResponse | null> {
  const data = await Api().getVehicle(vehicleId);
  if (!data || !data.id) {
    mapErrorMessage.value = i18n.global.t('pages.vehicleInstall.errors.getVehicle');
    setTimeout(() => router.push({ name: 'Vehicle' }), 3000);
    return null;
  }
  if (!data.device || !data.device.device_id) {
    mapErrorMessage.value = i18n.global.t('pages.vehicleInstall.errors.getDevice');
    return null;
  }
  return data;
}

async function getLatestVehiclePosition(deviceId: string): Promise<VehicleTimedPosition | null> {
  const latest = await Api().getLatestVehicleSubscription(deviceId, { topic: 'traffic' });
  const lat = parseFloat(latest.data.lat);
  const lng = parseFloat(latest.data.lon);
  const time = formatUnixToDateTimeWithMilliseconds(latest.data.tm, '/');
  if (Number.isNaN(lat) || Number.isNaN(lng) || !time) {
    return null;
  }
  return {
    position: { lat, lng },
    time,
    source: sourceType.INITIAL
  }
}

async function setUpTrafficSubscriber(deviceId: string): Promise<any> {
  return subscribeService().subscribeToTraffics({ device_id: deviceId }, (vehicle: Vehicle, payload: any) => {
    const lat = parseFloat(payload.lat);
    const lng = parseFloat(payload.lon);
    const time = payload.tm;
    const dgn = payload.dgn;
    if (Number.isNaN(lat) || Number.isNaN(lng) || !time) {
      return;
    }
    if (dgn && (!deliveryPlanId.value || deliveryPlanId.value !== dgn)) {
      deliveryPlanId.value = dgn;
    }
    vehiclePosition.value = {
      position: { lat, lng },
      time,
      source: sourceType.SUBSCRIBED
    }
  });
}

async function setVehicleAndPosition() {
  mapErrorMessage.value = '';
  if (!route.params.id) {
    return;
  }
  const vehicleId = Number(route.params.id);
  try {
    vehicleInfo.value = await getVehicleInfo(vehicleId);
    if (vehicleInfo.value === null) {
      isLoadingFinished.value = true;
      return;
    }
    if (vehicleInfo.value.operation_office?.tracking_service === TRACKING_SERVICE_DOCOMAP) {
      props.showFlash(i18n.global.t('pages.vehicleInstall.errors.usingDocomap'), 'error', false);
    }
    [vehiclePosition.value, subscriber.value] = await Promise.all([
      getLatestVehiclePosition(vehicleInfo.value.device.device_id),
      setUpTrafficSubscriber(vehicleInfo.value.device.device_id)
    ]);
    if (vehiclePosition.value === null) {
      return;
    }
    zoomLevel.value = 16;
    isMarkerVisible.value = true;
    isLoadingFinished.value = true;
  } catch (e: any) {
    console.error(e);
    mapErrorMessage.value = i18n.global.t('pages.vehicleInstall.errors.getLatestPosition');
    isLoadingFinished.value = true;
  }
}

async function setVehicleEvents() {
  eventVideoListErrorMessage.value = '';
  const deviceId = vehicleInfo.value?.device.device_id;
  if (!deviceId) {
    vehicleEvents.value = [];
    return;
  }
  const currentDate = getCurrentDate();
  const params = {
    start_date: currentDate,
    end_date: currentDate,
    event_types: DRIVING_ALERT_EVENT_TYPES
  }
  try {
    vehicleEvents.value = await Api().getVehicleEvents(deviceId, params);
    if (vehicleEvents.value.length === 0) {
      eventVideoListErrorMessage.value = i18n.global.t('pages.vehicleInstall.errors.noEventVideo');
    }
  } catch (e: any) {
    console.error(e);
    eventVideoListErrorMessage.value = i18n.global.t('pages.vehicleInstall.errors.getVideos');
  }
}

function getCurrentDate():string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0'); 
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

watch(() => vehiclePosition.value, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    zoomLevel.value = 16;
    isMarkerVisible.value = true;
    isLoadingFinished.value = true;
  }
});

watch(() => vehicleInfo.value?.device?.device_id, () => {
  setVehicleEvents();
});

onMounted(() => {
  Api()
    .getSelfAccount()
    .then((data) => {
      selfOfficeType.value = data?.login_organization_type ?? '';
      isManagementOffice.value = selfOfficeType.value == MANAGEMENT_OFFICE_TYPE;
      isSubManagementOffice.value = selfOfficeType.value == SUB_MANAGEMENT_OFFICE_TYPE;
      isInstallationOffice.value = selfOfficeType.value == INSTALLATION_OFFICE_TYPE;
    })
  setVehicleAndPosition();
});

onUnmounted(() => {
  subscriber.value?.unsubscribe();
});


return (_ctx: any,_cache: any) => {
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (isManagementOffice.value || isSubManagementOffice.value || isInstallationOffice.value)
        ? (_openBlock(), _createBlock(VehicleTabs, {
            key: 0,
            class: "mb-4"
          }))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h4", _hoisted_4, _toDisplayString(_unref(i18n).global.t('pages.vehicleInstall.title')), 1)
      ]),
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, {
            cols: "12",
            md: "9",
            class: "order-md-2 order-1"
          }, {
            default: _withCtx(() => [
              _createVNode(VehicleMap, {
                vehiclePosition: vehiclePosition.value,
                vehicleInfo: vehicleInfo.value,
                zoomLevel: zoomLevel.value,
                isMarkerVisible: isMarkerVisible.value,
                isLoadingFinished: isLoadingFinished.value,
                errorMessage: mapErrorMessage.value
              }, null, 8, ["vehiclePosition", "vehicleInfo", "zoomLevel", "isMarkerVisible", "isLoadingFinished", "errorMessage"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "3",
            class: "order-md-1 order-2"
          }, {
            default: _withCtx(() => [
              _createVNode(EventVideoList, {
                vehicleEvents: vehicleEvents.value,
                errorMessage: eventVideoListErrorMessage.value
              }, null, 8, ["vehicleEvents", "errorMessage"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ])
  ]))
}
}

})